import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const directorySchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${SITE_URL}/pu-belt-manufacturers-directory`,
    url: `${SITE_URL}/pu-belt-manufacturers-directory`,
    name: 'Top PU Timing Belt Manufacturers Directory – Global & India | True Passage',
    description: 'Comprehensive directory of leading polyurethane timing belt manufacturers worldwide and in India. Find verified PU belt suppliers for industrial power transmission.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
        { '@type': 'ListItem', position: 3, name: 'PU Belt Manufacturers', item: `${SITE_URL}/pu-belt-manufacturers-directory` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Top Global PU Timing Belt Manufacturers',
    description: 'Leading polyurethane timing belt manufacturers worldwide',
    numberOfItems: 10,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Gates Corporation', description: 'USA - Power transmission belts and industrial belt systems' },
      { '@type': 'ListItem', position: 2, name: 'Megadyne Group', description: 'Italy - Timing belt manufacturer for OEMs' },
      { '@type': 'ListItem', position: 3, name: 'BRECOflex', description: 'Germany/USA - Extensive polyurethane timing belt range' },
      { '@type': 'ListItem', position: 4, name: 'Optibelt', description: 'Germany - Industrial drive belts' },
      { '@type': 'ListItem', position: 5, name: 'Continental ContiTech', description: 'Germany - Power transmission and timing belts' },
      { '@type': 'ListItem', position: 6, name: 'Forbo Movement Systems', description: 'Germany/Switzerland - Conveyor and processing belts' },
      { '@type': 'ListItem', position: 7, name: 'Barbieri Group', description: 'Italy - Custom PU timing belt solutions' },
      { '@type': 'ListItem', position: 8, name: 'Tsubakimoto Chain', description: 'Japan - Timing drive systems' },
      { '@type': 'ListItem', position: 9, name: 'Mitsuboshi Belting', description: 'Japan - Timing and automotive drive belts' },
      { '@type': 'ListItem', position: 10, name: 'Fenner Precision', description: 'UK/USA - High-precision PU timing belts' },
    ],
  },
];

const PUBeltManufacturersPage = () => {
  const globalManufacturers = [
    {
      name: 'Gates Corporation',
      country: 'USA',
      products: ['Power transmission belts', 'Industrial belt systems', 'Timing belts'],
      industries: ['Automotive', 'Industrial manufacturing', 'Agriculture'],
      exportMarkets: 'Worldwide operations',
      website: 'https://www.gates.com',
    },
    {
      name: 'Megadyne Group',
      country: 'Italy',
      products: ['Timing belts', 'Power transmission belts', 'OEM belt solutions'],
      industries: ['Industrial equipment', 'Automotive', 'Manufacturing'],
      exportMarkets: 'Global OEM supply',
      website: 'https://www.megadynegroup.com',
    },
    {
      name: 'BRECOflex',
      country: 'Germany / USA',
      products: ['Polyurethane timing belts', 'Custom belt solutions', 'Drive belts'],
      industries: ['Automation', 'Packaging', 'Material handling'],
      exportMarkets: 'Europe, Americas, Asia',
      website: 'https://www.brecoflex.com',
    },
    {
      name: 'Optibelt',
      country: 'Germany',
      products: ['PU timing belts', 'Industrial drive belts', 'V-belts'],
      industries: ['Industrial machinery', 'Automotive', 'Agriculture'],
      exportMarkets: 'European and global markets',
      website: 'https://www.optibelt.com',
    },
    {
      name: 'Continental ContiTech',
      country: 'Germany',
      products: ['Power transmission belts', 'Timing belts', 'Conveyor belts'],
      industries: ['Automotive', 'Industrial', 'Mining'],
      exportMarkets: 'Worldwide distribution',
      website: 'https://www.contitech.de',
    },
    {
      name: 'Forbo Movement Systems',
      country: 'Germany / Switzerland',
      products: ['Conveyor belts', 'Processing belts', 'Power transmission belts'],
      industries: ['Food processing', 'Logistics', 'Manufacturing'],
      exportMarkets: 'Europe, Asia, Americas',
      website: 'https://www.forbo.com',
    },
    {
      name: 'Barbieri Group',
      country: 'Italy',
      products: ['PU timing belts', 'Custom belt solutions', 'Industrial belts'],
      industries: ['Packaging', 'Textile', 'Wood processing'],
      exportMarkets: 'European and international markets',
      website: 'https://www.barbierirubber.it',
    },
    {
      name: 'Tsubakimoto Chain',
      country: 'Japan',
      products: ['Timing drive systems', 'Power transmission products', 'Industrial chains'],
      industries: ['Automotive', 'Industrial machinery', 'Material handling'],
      exportMarkets: 'Global distribution network',
      website: 'https://www.tsubakimoto.com',
    },
    {
      name: 'Mitsuboshi Belting',
      country: 'Japan',
      products: ['Timing belts', 'Automotive drive belts', 'Industrial belts'],
      industries: ['Automotive', 'Industrial equipment', 'Agriculture'],
      exportMarkets: 'Asia, Americas, Europe',
      website: 'https://www.mitsuboshi.com',
    },
    {
      name: 'Fenner Precision',
      country: 'UK / USA',
      products: ['High-precision PU timing belts', 'Industrial automation belts'],
      industries: ['Industrial automation', 'Robotics', 'Precision machinery'],
      exportMarkets: 'Europe, Americas, Asia',
      website: 'https://www.fenner-precision.com',
    },
  ];

  const indiaManufacturers = [
    {
      name: 'Pix Transmissions',
      location: 'Mumbai',
      products: ['PU timing belts', 'V-belts', 'Conveyor belts', 'Power transmission belts'],
      industries: ['Automotive', 'Industrial machinery', 'Agriculture', 'Mining'],
      exportMarkets: 'Africa, Middle East, Southeast Asia',
      website: 'https://www.pix.in',
    },
    {
      name: 'Arihant Belting Pvt. Ltd.',
      location: 'Surat',
      products: ['PU timing belts', 'Industrial transmission belts', 'Custom belt solutions'],
      industries: ['Textile', 'Packaging', 'Manufacturing'],
      exportMarkets: 'Middle East, Africa',
      website: null,
    },
    {
      name: 'DKT Engineering Enterprises',
      location: 'India',
      products: ['Industrial PU timing belts', 'Power transmission belts', 'Conveyor belts'],
      industries: ['Manufacturing', 'Food processing', 'Packaging'],
      exportMarkets: 'Regional export markets',
      website: null,
    },
    {
      name: 'Star Belting',
      location: 'India',
      products: ['Polyurethane timing belts', 'Transmission belts', 'Industrial belts'],
      industries: ['Industrial machinery', 'Material handling', 'Automation'],
      exportMarkets: 'Domestic and regional markets',
      website: null,
    },
    {
      name: 'VSI Enterprises',
      location: 'India',
      products: ['PU timing belts', 'Industrial transmission belts', 'Belt accessories'],
      industries: ['Manufacturing', 'Packaging', 'Textile'],
      exportMarkets: 'India and neighboring countries',
      website: null,
    },
    {
      name: 'Power Belt India',
      location: 'India',
      products: ['Polyurethane timing belts', 'Industrial drive belts', 'Custom belts'],
      industries: ['Industrial equipment', 'Automation', 'Manufacturing'],
      exportMarkets: 'Domestic and export markets',
      website: null,
    },
    {
      name: 'Diwan Belting Transmission Company',
      location: 'Delhi',
      products: ['Industrial belts', 'PU timing belts', 'Power transmission products'],
      industries: ['Manufacturing', 'Industrial machinery', 'Material handling'],
      exportMarkets: 'North India and export markets',
      website: null,
    },
    {
      name: 'Naisha Power Transmission',
      location: 'India',
      products: ['PU timing belts', 'Industrial transmission belts', 'Belt systems'],
      industries: ['Industrial automation', 'Packaging', 'Manufacturing'],
      exportMarkets: 'Regional markets',
      website: null,
    },
    {
      name: 'Jigna Sales Corporation',
      location: 'India',
      products: ['Industrial timing belts', 'PU belts', 'Transmission components'],
      industries: ['Manufacturing', 'Industrial equipment', 'Automation'],
      exportMarkets: 'Domestic and regional export',
      website: null,
    },
    {
      name: 'Bindal Belting',
      location: 'India',
      products: ['PU timing belts', 'Industrial power transmission belts', 'Belt accessories'],
      industries: ['Industrial machinery', 'Manufacturing', 'Material handling'],
      exportMarkets: 'India and neighboring regions',
      website: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Top PU Timing Belt Manufacturers Directory – Global & India | True Passage"
        description="Comprehensive directory of leading polyurethane timing belt manufacturers worldwide and in India. Find verified PU belt suppliers for industrial power transmission, conveyor systems, and automation."
        keywords="PU belt manufacturers, timing belt manufacturers, polyurethane belts, industrial belts, power transmission belts, conveyor belts, India belt manufacturers, global belt suppliers"
        canonical="/pu-belt-manufacturers-directory"
        schema={directorySchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-secondary to-navy-primary overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Top PU Timing Belt
              <br />
              <span className="text-gold-accent">Manufacturers Directory</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Comprehensive directory of leading polyurethane timing belt manufacturers worldwide and in India
            </p>
          </motion.div>
        </div>
      </section>

      {/* Global Manufacturers Section */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-navy-primary mb-4">
              Global PU Timing Belt Manufacturers
            </h2>
            <p className="text-lg text-navy-primary/70 max-w-3xl mx-auto">
              These companies are widely recognized in the industrial power transmission belt market
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {globalManufacturers.map((manufacturer, index) => (
              <motion.div
                key={manufacturer.name}
                className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-navy-primary mb-2">
                      {manufacturer.name}
                    </h3>
                    <div className="flex items-center gap-2 text-navy-primary/70">
                      <i className="ri-map-pin-line"></i>
                      <span className="text-sm font-medium">{manufacturer.country}</span>
                    </div>
                  </div>
                  {manufacturer.website && (
                    <a
                      href={manufacturer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gold-accent/10 rounded-lg flex items-center justify-center hover:bg-gold-accent/20 transition-colors"
                    >
                      <i className="ri-external-link-line text-gold-accent"></i>
                    </a>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-box-3-line text-gold-accent"></i>
                      Products
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {manufacturer.products.map((product) => (
                        <span
                          key={product}
                          className="px-3 py-1 bg-navy-primary/5 text-navy-primary text-xs rounded-full"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-building-line text-gold-accent"></i>
                      Industries Served
                    </h4>
                    <p className="text-sm text-navy-primary/70">
                      {manufacturer.industries.join(', ')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-global-line text-gold-accent"></i>
                      Export Markets
                    </h4>
                    <p className="text-sm text-navy-primary/70">
                      {manufacturer.exportMarkets}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* India Manufacturers Section */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-navy-primary mb-4">
              PU Timing Belt Manufacturers in India
            </h2>
            <p className="text-lg text-navy-primary/70 max-w-3xl mx-auto">
              Active manufacturers and major suppliers of PU timing belts and industrial power transmission belts
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {indiaManufacturers.map((manufacturer, index) => (
              <motion.div
                key={manufacturer.name}
                className="bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-navy-primary mb-2">
                      {manufacturer.name}
                    </h3>
                    <div className="flex items-center gap-2 text-navy-primary/70">
                      <i className="ri-map-pin-line"></i>
                      <span className="text-sm font-medium">{manufacturer.location}</span>
                    </div>
                  </div>
                  {manufacturer.website && (
                    <a
                      href={manufacturer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gold-accent/10 rounded-lg flex items-center justify-center hover:bg-gold-accent/20 transition-colors"
                    >
                      <i className="ri-external-link-line text-gold-accent"></i>
                    </a>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-box-3-line text-gold-accent"></i>
                      Products
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {manufacturer.products.map((product) => (
                        <span
                          key={product}
                          className="px-3 py-1 bg-navy-primary/5 text-navy-primary text-xs rounded-full"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-building-line text-gold-accent"></i>
                      Industries Served
                    </h4>
                    <p className="text-sm text-navy-primary/70">
                      {manufacturer.industries.join(', ')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-navy-primary mb-2 flex items-center gap-2">
                      <i className="ri-global-line text-gold-accent"></i>
                      Export Markets
                    </h4>
                    <p className="text-sm text-navy-primary/70">
                      {manufacturer.exportMarkets}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* True Passage CTA Section */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="bg-gradient-to-br from-navy-primary to-navy-secondary rounded-3xl p-12 lg:p-16"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-white mb-5">
                Source PU Belts Through True Passage
              </h2>
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                True Passage can help you procure polyurethane timing belts from verified manufacturers listed above and ship them directly to your location. We handle supplier vetting, quality assurance, logistics, and customs clearance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/request-quote"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gold-accent text-navy-primary rounded-full font-semibold text-base hover:scale-105 transition-transform shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Request Quote
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-full font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap"
                >
                  Our Services
                  <i className="ri-service-line"></i>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PUBeltManufacturersPage;